// =============================================================================
// CONFIRM DIALOG — reusable, accessible replacement for window.confirm().
//
// The native browser confirm() is jarring (system dialog, no theming, no
// async support) and several flows want a proper "Are you sure?" before a
// destructive action. This component renders a centered modal matching
// the Platform's surface-* / glass-panel styling, supports a `tone` prop
// (default | danger) for color, an async onConfirm callback with its own
// loading state, and closes on backdrop click + Escape.
//
// Usage:
//   const [open, setOpen] = useState(false);
//   <ConfirmDialog
//     open={open}
//     onClose={() => setOpen(false)}
//     onConfirm={async () => { await doTheThing(); }}
//     title="Delete all guest accounts?"
//     description="This will permanently delete …"
//     confirmLabel="Yes, run cleanup"
//     tone="danger"
//   />
// =============================================================================

import { useEffect, useState } from 'react';
import { AlertTriangle, X } from 'lucide-react';
import { Spinner } from '@/components/shared/UIComponents';

export interface ConfirmDialogProps {
  open: boolean;
  onClose: () => void;
  /** Fired when the user clicks the confirm button. May return a promise — the
   *  dialog shows a spinner on the button until it resolves, then closes on
   *  success (throws stay open so the caller can surface an error). */
  onConfirm: () => void | Promise<void>;
  title: string;
  /** Body copy. Pass a string or a ReactNode if you need formatting. */
  description?: React.ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  /** "danger" styles the confirm button red. */
  tone?: 'default' | 'danger';
}

export function ConfirmDialog({
  open,
  onClose,
  onConfirm,
  title,
  description,
  confirmLabel = 'Confirm',
  cancelLabel  = 'Cancel',
  tone         = 'default',
}: ConfirmDialogProps) {
  const [submitting, setSubmitting] = useState(false);

  // Esc-to-close
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !submitting) onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, submitting, onClose]);

  if (!open) return null;

  const handleConfirm = async () => {
    setSubmitting(true);
    try {
      await onConfirm();
      // Success → close. Errors stay open so the caller can show an
      // error banner in the page; the dialog re-enables the buttons.
      onClose();
    } catch (e) {
      console.error('[ConfirmDialog] onConfirm threw:', e);
    } finally {
      setSubmitting(false);
    }
  };

  const confirmBtnClass =
    tone === 'danger'
      ? 'bg-red-600 hover:bg-red-700 text-white'
      : 'bg-brand-600 hover:bg-brand-700 text-white';

  return (
    <div
      className="fixed inset-0 z-[80] flex items-center justify-center px-4 py-6 bg-black/50 backdrop-blur-sm animate-in fade-in duration-150"
      onClick={() => { if (!submitting) onClose(); }}
      role="presentation"
    >
      <div
        className="relative w-full max-w-md rounded-2xl bg-surface-0 border border-surface-200 shadow-2xl animate-in zoom-in-95 duration-200"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="confirm-dialog-title"
      >
        {/* Header */}
        <div className="px-5 py-4 border-b border-surface-200 flex items-start gap-3">
          <div
            className={`w-9 h-9 rounded-full flex items-center justify-center shrink-0 ${
              tone === 'danger'
                ? 'bg-red-100 text-red-600'
                : 'bg-brand-100 text-brand-600'
            }`}
          >
            <AlertTriangle size={18} />
          </div>
          <div className="flex-1 min-w-0">
            <h3 id="confirm-dialog-title" className="text-base font-semibold text-surface-900">
              {title}
            </h3>
          </div>
          <button
            onClick={onClose}
            disabled={submitting}
            className="p-1 rounded-md hover:bg-surface-100 text-surface-500 hover:text-surface-900 disabled:opacity-50"
            aria-label="Close"
          >
            <X size={16} />
          </button>
        </div>

        {/* Body */}
        {description && (
          <div className="px-5 py-4 text-sm text-surface-600 leading-relaxed">
            {description}
          </div>
        )}

        {/* Footer */}
        <div className="px-5 py-3 border-t border-surface-200 flex items-center justify-end gap-2">
          <button
            onClick={onClose}
            disabled={submitting}
            className="px-3 py-1.5 rounded-md text-sm font-medium bg-surface-100 hover:bg-surface-200 text-surface-900 disabled:opacity-50"
          >
            {cancelLabel}
          </button>
          <button
            onClick={handleConfirm}
            disabled={submitting}
            className={`px-3 py-1.5 rounded-md text-sm font-medium inline-flex items-center gap-1.5 disabled:opacity-50 ${confirmBtnClass}`}
          >
            {submitting && <Spinner size="sm" />}
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
